import React, { ReactElement, ReactNode } from "react";
import { useGetArticleTagsQuery } from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";
import { Filters } from "../../filters/Filters";

export interface ArticleTagsPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never)) => void;

    shouldShowDropdown?: boolean;
    icon?: ReactNode;
    searchMinimum?: number;
    toggleClassName?: string;
    toggleText?: string;
}

const ArticleTags = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable,
    shouldShowDropdown, icon, searchMinimum, toggleClassName, toggleText,
}: ArticleTagsPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetArticleTagsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<string>[] : Option<string>;
    type ValueType = TIsMulti extends true ? Option<string>[] : Option<string>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<string>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.articleTags?.map(tag => ({
        label: tag.title,
        value: tag.id,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    if (shouldShowDropdown) {
        return (
            <Filters
                icon={icon}
                toggleText={toggleText}
                isMulti={isMulti}
                categories={values.map(row => ({
                    value: row.value,
                    label: row.label as string,
                }))}
                searchMinimum={searchMinimum}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onSelected as ((value: string[]) => void))(Array.from(current))
                    : (onSelected as ((value: string) => void))(current as string))}
            />
        );
    }

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti ?? true as TIsMulti}
            onChange={handleSelected}
            options={values ?? []}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}
        />
    );
});

ArticleTags.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    isClearable: undefined,

    shouldShowDropdown: false,
    icon: undefined,
    searchMinimum: undefined,
    toggleClassName: "",
    toggleText: undefined,
};

export default ArticleTags as <
    TIsMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: ArticleTagsPickerProps<TIsMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof ArticleTags>;
